import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, Trash2, Download, Send, CheckCircle, ArrowLeft } from "lucide-react";
import { API, BUSINESS } from "../data/site";

const STATUS_STYLES = {
    draft: "bg-stone-200 text-stone-600",
    sent: "bg-forest/10 text-forest",
    paid: "bg-forest text-bone",
    partially_paid: "bg-ochre/25 text-clay",
    overdue: "bg-clay/20 text-clay",
    cancelled: "bg-stone-200 text-stone-500 line-through",
};
const invLabel = (s) => (s || "draft").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const num = (v) => parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;
const fmt = (n) => n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayStr = new Date().toISOString().split("T")[0];

const inputCls = "w-full rounded-lg border border-forest/20 bg-white px-3 py-2 text-sm text-ink outline-none transition-colors focus:border-forest disabled:cursor-not-allowed disabled:bg-bone-subtle";
const lbl = "eyebrow mb-1 block text-forest";

export const InvoiceModal = ({ sourceType, sourceId, invoice, readOnly = false, token, onClose, onDeleted }) => {
    const [inv, setInv] = useState(invoice || null);
    const [step, setStep] = useState("edit"); // edit | send | payment
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState(
        "Thanks for choosing Lawn Busters. Please find your invoice attached — payment details are on the invoice. Any questions, just give us a call."
    );
    const [pay, setPay] = useState({ status: "paid", payment_date: todayStr, payment_method: "Bank transfer", amount_paid: "" });
    const [busy, setBusy] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        if (invoice) {
            setSubject(`Invoice ${invoice.invoice_no} from Lawn Busters`);
            return;
        }
        (async () => {
            try {
                const { data } = readOnly
                    ? await axios.get(`${API}/invoices/by-source/${sourceId}`, { headers })
                    : await axios.post(`${API}/invoices`, { source_type: sourceType, source_id: sourceId }, { headers });
                setInv(data);
                setSubject(`Invoice ${data.invoice_no} from Lawn Busters`);
            } catch (err) {
                if (err.response?.status === 404) toast.error("No invoice has been created for this yet.");
                else toast.error("Could not load the invoice.");
                onClose();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totals = useMemo(() => {
        if (!inv) return { sub: 0, disc: 0, gst: 0, total: 0 };
        const sub = inv.items.reduce((a, i) => a + num(i.price), 0);
        const disc = num(inv.discount);
        const after = Math.max(sub - disc, 0);
        const gst = inv.gst_applicable ? after * (num(inv.gst_rate) || 10) / 100 : 0;
        return { sub, disc, gst, total: after + gst };
    }, [inv]);

    if (!inv) {
        return (
            <div className="fixed inset-0 z-[80] flex items-center justify-center bg-forest-night/60 backdrop-blur-sm">
                <p className="rounded-full bg-bone px-6 py-3 text-sm font-bold text-forest">Loading invoice...</p>
            </div>
        );
    }

    const upd = (key, value) => setInv({ ...inv, [key]: value });
    const updItem = (i, key, value) => {
        const items = inv.items.map((it, x) => (x === i ? { ...it, [key]: value } : it));
        setInv({ ...inv, items });
    };
    const status = inv.status_display || inv.status;

    const save = async () => {
        setBusy(true);
        try {
            const { data } = await axios.patch(`${API}/invoices/${inv.id}`, {
                customer_name: inv.customer_name, customer_email: inv.customer_email,
                customer_phone: inv.customer_phone, customer_address: inv.customer_address,
                job_address: inv.job_address, items: inv.items, discount: inv.discount,
                gst_applicable: inv.gst_applicable, gst_rate: inv.gst_rate,
                issue_date: inv.issue_date, due_date: inv.due_date,
                notes: inv.notes, payment_info: inv.payment_info, status: inv.status,
            }, { headers });
            setInv(data);
            toast.success("Invoice saved.");
        } catch {
            toast.error("Could not save the invoice.");
        } finally {
            setBusy(false);
        }
    };

    const downloadPdf = async () => {
        try {
            const { data } = await axios.get(`${API}/invoices/${inv.id}/pdf`, { headers, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `${inv.invoice_no}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Could not download the PDF.");
        }
    };

    const send = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/invoices/${inv.id}/send`, { subject, message }, { headers });
            setInv({ ...inv, status: "sent", status_display: "sent", sent_at: data.sent_at, sent_to: inv.customer_email });
            setStep("edit");
            toast.success(`Invoice sent to ${inv.customer_email}`);
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not send the invoice.");
        } finally {
            setBusy(false);
        }
    };

    const recordPayment = async () => {
        setBusy(true);
        try {
            await axios.post(`${API}/invoices/${inv.id}/payment`, { ...pay, amount_paid: pay.amount_paid || String(totals.total) }, { headers });
            setInv({ ...inv, status: pay.status, status_display: pay.status, payment_date: pay.payment_date, payment_method: pay.payment_method, amount_paid: pay.amount_paid || String(totals.total) });
            setStep("edit");
            toast.success(`Marked as ${invLabel(pay.status)}.`);
        } catch {
            toast.error("Could not record the payment.");
        } finally {
            setBusy(false);
        }
    };

    const removeInvoice = async () => {
        if (!window.confirm(`Delete invoice ${inv.invoice_no} permanently? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/invoices/${inv.id}`, { headers });
            toast.success(`Invoice ${inv.invoice_no} deleted.`);
            if (onDeleted) onDeleted(inv.id);
            else onClose();
        } catch {
            toast.error("Could not delete the invoice.");
        }
    };

    return (
        <div data-testid="invoice-modal" className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-forest-night/60 p-4 backdrop-blur-sm">
            <div className="my-6 w-full max-w-6xl">
                <div className="mb-4 flex flex-wrap items-center gap-3">
                    <p className="font-display text-lg font-bold text-bone">Invoice {inv.invoice_no}</p>
                    <span data-testid="invoice-status-badge" className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
                        {invLabel(status)}
                    </span>
                    {inv.sent_at && (
                        <span data-testid="invoice-sent-stamp" className="text-[11px] font-semibold text-bone/70">
                            Sent {new Date(inv.sent_at).toLocaleString("en-AU")} to {inv.sent_to}
                        </span>
                    )}
                    <button onClick={onClose} data-testid="invoice-close-button" aria-label="Close"
                        className="ml-auto flex items-center gap-2 rounded-full border border-bone/40 px-5 py-2.5 text-xs font-bold text-bone hover:bg-bone/10">
                        <X size={14} /> Close
                    </button>
                </div>

                <div className={`grid gap-6 ${readOnly ? "" : "lg:grid-cols-2"}`}>
                    {/* -------- Left: editor / send / payment -------- */}
                    {!readOnly && (
                        <div data-testid="invoice-editor" className="rounded-3xl bg-bone-card p-6 shadow-2xl">
                            {step === "edit" && (
                                <>
                                    <p className="eyebrow text-forest-leaf">Customer</p>
                                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                                        <div><label className={lbl}>Name</label><input data-testid="invoice-customer-name" className={inputCls} value={inv.customer_name} onChange={(e) => upd("customer_name", e.target.value)} /></div>
                                        <div><label className={lbl}>Email</label><input data-testid="invoice-customer-email" className={inputCls} value={inv.customer_email} onChange={(e) => upd("customer_email", e.target.value)} /></div>
                                        <div><label className={lbl}>Phone</label><input data-testid="invoice-customer-phone" className={inputCls} value={inv.customer_phone} onChange={(e) => upd("customer_phone", e.target.value)} /></div>
                                        <div><label className={lbl}>Customer address</label><input data-testid="invoice-customer-address" className={inputCls} value={inv.customer_address} onChange={(e) => upd("customer_address", e.target.value)} /></div>
                                        <div className="sm:col-span-2"><label className={lbl}>Job / property address</label><input data-testid="invoice-job-address" className={inputCls} value={inv.job_address} onChange={(e) => upd("job_address", e.target.value)} /></div>
                                    </div>

                                    <p className="eyebrow mt-5 text-forest-leaf">Dates</p>
                                    <div className="mt-2 grid grid-cols-2 gap-3">
                                        <div><label className={lbl}>Issue date</label><input type="date" data-testid="invoice-issue-date-input" className={inputCls} value={inv.issue_date} onChange={(e) => upd("issue_date", e.target.value)} /></div>
                                        <div><label className={lbl}>Due date</label><input type="date" data-testid="invoice-due-date-input" className={inputCls} value={inv.due_date} onChange={(e) => upd("due_date", e.target.value)} /></div>
                                    </div>

                                    <div className="mt-5 flex items-center justify-between">
                                        <p className="eyebrow text-forest-leaf">Services &amp; prices</p>
                                        <button onClick={() => upd("items", [...inv.items, { description: "", price: "" }])} data-testid="invoice-add-item"
                                            className="flex items-center gap-1.5 rounded-full bg-forest px-3 py-1.5 text-[11px] font-bold text-bone hover:bg-forest-deep">
                                            <Plus size={12} /> Add line
                                        </button>
                                    </div>
                                    <div className="mt-2 space-y-2">
                                        {inv.items.map((it, i) => (
                                            <div key={i} data-testid={`invoice-item-row-${i}`} className="flex items-center gap-2">
                                                <input className={inputCls} placeholder="Service description" value={it.description}
                                                    data-testid={`invoice-item-desc-${i}`} onChange={(e) => updItem(i, "description", e.target.value)} />
                                                <input className={`${inputCls} w-24 text-right`} placeholder="0.00" value={it.price}
                                                    data-testid={`invoice-item-price-${i}`} onChange={(e) => updItem(i, "price", e.target.value)} />
                                                <button onClick={() => upd("items", inv.items.filter((_, x) => x !== i))} data-testid={`invoice-item-remove-${i}`} aria-label="Remove line"
                                                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-clay/40 text-clay hover:bg-clay/10">
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4 grid grid-cols-2 gap-3">
                                        <div><label className={lbl}>Discount ($)</label><input data-testid="invoice-discount-input" className={inputCls} value={inv.discount} onChange={(e) => upd("discount", e.target.value)} placeholder="0" /></div>
                                        <div>
                                            <label className={lbl}>GST</label>
                                            <div className="flex items-center gap-2">
                                                <button type="button" onClick={() => upd("gst_applicable", !inv.gst_applicable)} data-testid="invoice-gst-toggle" aria-pressed={inv.gst_applicable}
                                                    className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${inv.gst_applicable ? "bg-forest text-bone" : "border border-forest/20 text-forest"}`}>
                                                    {inv.gst_applicable ? "GST on" : "No GST"}
                                                </button>
                                                {inv.gst_applicable && (
                                                    <input className={`${inputCls} w-20`} data-testid="invoice-gst-rate-input" value={inv.gst_rate} onChange={(e) => upd("gst_rate", e.target.value)} placeholder="10" />
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <label className={lbl}>Payment information (bank details etc.)</label>
                                        <textarea rows={2} data-testid="invoice-payment-info-input" className={inputCls} value={inv.payment_info} onChange={(e) => upd("payment_info", e.target.value)} placeholder="e.g. Bank transfer — BSB xxx xxx, Acc xxxx xxxx" />
                                    </div>
                                    <div className="mt-3">
                                        <label className={lbl}>Notes on invoice</label>
                                        <textarea rows={2} data-testid="invoice-notes-input" className={inputCls} value={inv.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="e.g. deposit received, payment terms" />
                                    </div>

                                    <div className="mt-6 flex flex-wrap gap-2.5">
                                        <button onClick={save} disabled={busy} data-testid="invoice-save-button"
                                            className="rounded-full bg-forest px-6 py-2.5 text-xs font-bold text-bone hover:bg-forest-deep disabled:opacity-60">
                                            {busy ? "Saving..." : "Save Invoice"}
                                        </button>
                                        <button onClick={downloadPdf} data-testid="invoice-download-pdf-button"
                                            className="flex items-center gap-1.5 rounded-full border border-forest/25 px-4 py-2.5 text-xs font-bold text-forest hover:bg-bone-subtle">
                                            <Download size={13} /> Download PDF
                                        </button>
                                        <button onClick={() => setStep("send")} data-testid="invoice-send-open-button"
                                            className="flex items-center gap-1.5 rounded-full bg-ochre px-4 py-2.5 text-xs font-bold text-forest-night hover:bg-clay hover:text-bone">
                                            <Send size={13} /> {inv.status === "draft" ? "Send Invoice" : "Resend Invoice"}
                                        </button>
                                        <button onClick={() => setStep("payment")} data-testid="invoice-mark-paid-button"
                                            className="flex items-center gap-1.5 rounded-full border border-forest/25 px-4 py-2.5 text-xs font-bold text-forest hover:bg-bone-subtle">
                                            <CheckCircle size={13} /> Record Payment
                                        </button>
                                        <button onClick={removeInvoice} data-testid="invoice-delete-button"
                                            className="ml-auto flex items-center gap-1.5 rounded-full border border-clay/40 px-4 py-2.5 text-xs font-bold text-clay hover:bg-clay/10">
                                            <Trash2 size={13} /> Delete
                                        </button>
                                    </div>
                                </>
                            )}

                            {step === "send" && (
                                <div data-testid="invoice-send-step">
                                    <p className="eyebrow text-forest-leaf">Review before sending</p>
                                    <div className="mt-3 space-y-3">
                                        <div>
                                            <label className={lbl}>Recipient</label>
                                            <p data-testid="invoice-recipient" className="rounded-lg border border-forest/15 bg-bone-subtle px-3 py-2 text-sm font-semibold text-ink">
                                                {inv.customer_email || "No email saved for this customer"}
                                            </p>
                                        </div>
                                        <div>
                                            <label className={lbl}>Subject</label>
                                            <input data-testid="invoice-subject-input" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} />
                                        </div>
                                        <div>
                                            <label className={lbl}>Email message</label>
                                            <textarea rows={5} data-testid="invoice-message-input" className={inputCls} value={message} onChange={(e) => setMessage(e.target.value)} />
                                        </div>
                                        <p data-testid="invoice-attachment-note" className="flex items-center gap-2 rounded-lg bg-forest/5 px-3 py-2 text-xs font-semibold text-forest">
                                            <Download size={13} /> Attached: {inv.invoice_no}.pdf (generated from the preview)
                                        </p>
                                    </div>
                                    <div className="mt-5 flex gap-2.5">
                                        <button onClick={() => setStep("edit")} data-testid="invoice-send-back"
                                            className="flex items-center gap-1.5 rounded-full border border-forest/25 px-4 py-2.5 text-xs font-bold text-forest hover:bg-bone-subtle">
                                            <ArrowLeft size={13} /> Back
                                        </button>
                                        <button onClick={send} disabled={busy || !inv.customer_email} data-testid="invoice-send-confirm-button"
                                            className="flex items-center gap-1.5 rounded-full bg-forest px-6 py-2.5 text-xs font-bold text-bone hover:bg-forest-deep disabled:opacity-60">
                                            <Send size={13} /> {busy ? "Sending..." : "Send Invoice"}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {step === "payment" && (
                                <div data-testid="invoice-payment-form">
                                    <p className="eyebrow text-forest-leaf">Record payment</p>
                                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                        <div>
                                            <label className={lbl}>Status</label>
                                            <select data-testid="invoice-payment-status" className={inputCls} value={pay.status} onChange={(e) => setPay({ ...pay, status: e.target.value })}>
                                                <option value="paid">Paid</option>
                                                <option value="partially_paid">Partially Paid</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className={lbl}>Payment date</label>
                                            <input type="date" data-testid="invoice-payment-date" className={inputCls} value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className={lbl}>Payment method</label>
                                            <select data-testid="invoice-payment-method" className={inputCls} value={pay.payment_method} onChange={(e) => setPay({ ...pay, payment_method: e.target.value })}>
                                                {["Bank transfer", "Cash", "Card", "Other"].map((m) => <option key={m} value={m}>{m}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={lbl}>Amount received ($)</label>
                                            <input data-testid="invoice-payment-amount" className={inputCls} value={pay.amount_paid} onChange={(e) => setPay({ ...pay, amount_paid: e.target.value })} placeholder={fmt(totals.total)} />
                                        </div>
                                    </div>
                                    <div className="mt-5 flex gap-2.5">
                                        <button onClick={() => setStep("edit")} data-testid="invoice-payment-back"
                                            className="flex items-center gap-1.5 rounded-full border border-forest/25 px-4 py-2.5 text-xs font-bold text-forest hover:bg-bone-subtle">
                                            <ArrowLeft size={13} /> Back
                                        </button>
                                        <button onClick={recordPayment} disabled={busy} data-testid="invoice-payment-confirm"
                                            className="flex items-center gap-1.5 rounded-full bg-forest px-6 py-2.5 text-xs font-bold text-bone hover:bg-forest-deep disabled:opacity-60">
                                            <CheckCircle size={13} /> {busy ? "Saving..." : "Record Payment"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* -------- Right: preview -------- */}
                    <div data-testid="invoice-preview" className={readOnly ? "mx-auto w-full max-w-2xl" : ""}>
                        <div className="invoice-sheet overflow-hidden rounded-2xl bg-white shadow-2xl">
                            <div className="flex items-center justify-between bg-forest px-7 py-6 text-bone">
                                <div className="flex items-center gap-3">
                                    <img src="/assets/photos/logo.png" alt="Lawn Busters" className="h-11 w-auto" />
                                    <div>
                                        <p className="font-display text-xl font-bold leading-tight">Lawn Busters</p>
                                        <p className="text-[10px] text-bone/70">Adelaide&apos;s Local Property Care Experts</p>
                                        <p className="text-[10px] text-bone/70">{BUSINESS.phone} · {BUSINESS.email}</p>
                                    </div>
                                </div>
                                <p className="font-display text-2xl font-bold text-ochre">INVOICE</p>
                            </div>
                            <div className="px-7 py-6">
                                <div className="grid grid-cols-2 gap-4 border-b border-forest/10 pb-5 sm:grid-cols-4">
                                    {[["Invoice No", inv.invoice_no], ["Issue Date", inv.issue_date], ["Due Date", inv.due_date], ["Status", invLabel(status)]].map(([k, v]) => (
                                        <div key={k}>
                                            <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-moss">{k}</p>
                                            <p className="mt-1 text-sm font-bold text-ink">{v}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                    <div>
                                        <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-moss">Bill To</p>
                                        <p className="mt-1.5 text-sm font-bold text-ink">{inv.customer_name}</p>
                                        {inv.customer_address && <p className="text-xs text-moss">{inv.customer_address}</p>}
                                        {inv.customer_phone && <p className="text-xs text-moss">{inv.customer_phone}</p>}
                                        {inv.customer_email && <p className="text-xs text-moss">{inv.customer_email}</p>}
                                    </div>
                                    <div>
                                        <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-moss">Job / Property Address</p>
                                        <p className="mt-1.5 text-sm text-ink">{inv.job_address || inv.customer_address || "—"}</p>
                                        {inv.job_date && <p className="mt-1 text-xs text-moss">Job date: {inv.job_date}</p>}
                                    </div>
                                </div>
                                <table className="mt-6 w-full text-sm">
                                    <thead>
                                        <tr className="bg-bone-subtle text-left">
                                            <th className="rounded-l-lg px-3 py-2 font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-forest">Service / Description</th>
                                            <th className="rounded-r-lg px-3 py-2 text-right font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-forest">Price</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inv.items.map((it, i) => (
                                            <tr key={i} className="border-b border-forest/8">
                                                <td className="px-3 py-2.5 text-ink">{it.description || "—"}</td>
                                                <td className="px-3 py-2.5 text-right text-moss">${fmt(num(it.price))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="mt-4 space-y-1.5 text-sm">
                                    <div className="flex justify-end gap-8"><span className="text-moss">Subtotal</span><span className="w-24 text-right text-ink">${fmt(totals.sub)}</span></div>
                                    {totals.disc > 0 && <div className="flex justify-end gap-8"><span className="text-moss">Discount</span><span className="w-24 text-right text-ink">-${fmt(totals.disc)}</span></div>}
                                    {inv.gst_applicable && <div className="flex justify-end gap-8"><span className="text-moss">GST ({num(inv.gst_rate) || 10}%)</span><span className="w-24 text-right text-ink">${fmt(totals.gst)}</span></div>}
                                </div>
                                <div className="mt-3 flex items-center justify-between rounded-xl bg-forest px-4 py-3 text-bone">
                                    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em]">Total Due</span>
                                    <span data-testid="invoice-total" className="font-display text-xl font-bold text-ochre">${fmt(totals.total)}</span>
                                </div>
                                <div className="mt-5">
                                    <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-moss">Payment Information</p>
                                    <p className="mt-1.5 whitespace-pre-line text-xs leading-relaxed text-ink">{inv.payment_info || "Payment details provided on request."}</p>
                                    {(inv.status === "paid" || inv.status === "partially_paid") && (
                                        <p className="mt-2 inline-block rounded-lg bg-forest/10 px-3 py-1.5 text-xs font-bold text-forest">
                                            {invLabel(inv.status)}{inv.payment_date && ` — ${inv.payment_date}`}{inv.payment_method && ` via ${inv.payment_method}`}
                                        </p>
                                    )}
                                </div>
                                {inv.notes && (
                                    <div className="mt-4">
                                        <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.15em] text-moss">Notes</p>
                                        <p className="mt-1.5 whitespace-pre-line text-xs leading-relaxed text-ink">{inv.notes}</p>
                                    </div>
                                )}
                                <div className="mt-7 border-t border-forest/10 pt-4 text-center">
                                    <p className="text-xs font-semibold text-ink">Thank you for choosing Lawn Busters</p>
                                    <p className="mt-0.5 text-[10px] text-moss">{BUSINESS.phone} · {BUSINESS.email} · Adelaide, South Australia</p>
                                </div>
                            </div>
                        </div>
                        {readOnly && (
                            <button onClick={downloadPdf} data-testid="invoice-download-pdf-button"
                                className="mt-4 flex items-center gap-1.5 rounded-full bg-bone px-5 py-2.5 text-xs font-bold text-forest hover:bg-white">
                                <Download size={13} /> Download PDF
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

